import { Check, Eye, Image as ImageIcon, Sparkles, Wand2, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'

import {
  useApplyThumbnail,
  useGenerateThumbnail,
  useSuggestThumbnailText,
} from '@/api/endpoints/thumbnail'
import { thumbnailUrl } from '@/api/endpoints/media'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ThumbnailStyleCarousel } from '@/components/forms/ThumbnailStyleCarousel/ThumbnailStyleCarousel'
import { errorMessage } from '@/lib/errors'
import styles from './ThumbDock.module.scss'

export interface ThumbDockProps {
  videoId: string
  language: string
  channel: string
  /** Current ``thumbnail.png`` of the project — relative path under output_dir. */
  thumbnailPath: string | null
  /** Existing ``metadata.thumbnail_text`` to seed the input. */
  defaultText?: string
}

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/

/**
 * Compose a new project thumbnail. Single-column layout:
 *   - style carousel + text + color picker + actions
 *   - "Ver thumb no vídeo" button asks the player above to seek to t=0.
 *     The composed MP4 prepends the thumbnail frame at the start, so the
 *     player landing on currentTime=0 IS the thumbnail. Cheaper and more
 *     contextual than a side-by-side miniature.
 *
 * The flow is preview-then-apply: ``/tools/thumbnail`` stages a PNG and
 * returns ``thumb_path``; we hold it in local state and pass it back to
 * ``/tools/thumbnail-apply`` when the user confirms. This is the legacy
 * shape — the future ``tool_jobs`` system (see roadmap) will replace it
 * with opaque ``job_id`` tokens, server-side cleanup, and a gallery of
 * up to 5 staged previews per video.
 */
export function ThumbDock({
  videoId,
  language,
  channel,
  thumbnailPath: _thumbnailPath,
  defaultText = '',
}: ThumbDockProps) {
  const systemVideo = `${language}/${videoId}`

  const [text, setText] = useState(defaultText)
  const [highlightColor, setHighlightColor] = useState('#FFD700')
  const [hexInput, setHexInput] = useState('#FFD700')
  const [hexError, setHexError] = useState<string | null>(null)
  const [style, setStyle] = useState<string>('')
  const [preview, setPreview] = useState<{ url: string; thumbPath: string; durationS: number } | null>(
    null,
  )

  const generate = useGenerateThumbnail()
  const suggest = useSuggestThumbnailText()
  const apply = useApplyThumbnail()

  function handleHexBlur(value: string) {
    if (HEX_REGEX.test(value)) {
      setHighlightColor(value.toUpperCase())
      setHexError(null)
    } else {
      setHexError('Use #RRGGBB (ex: #FFD700)')
    }
  }

  function onGenerate(e: FormEvent) {
    e.preventDefault()
    if (!text.trim()) {
      toast.error('Texto é obrigatório')
      return
    }
    const input: Parameters<typeof generate.mutate>[0] = {
      channel,
      systemVideo,
      text: text.trim(),
      highlightColor,
    }
    if (style) input.style = style
    generate.mutate(input, {
        onSuccess: (data) => {
          setPreview({
            url: thumbnailUrl(data.thumb_path) ?? '',
            thumbPath: data.thumb_path,
            durationS: data.duration_s,
          })
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Não foi possível gerar a prévia.'))
        },
      },
    )
  }

  function onSuggest() {
    suggest.mutate(
      { channel, systemVideo },
      {
        onSuccess: (data) => {
          if (data.text.trim()) {
            setText(data.text.trim())
            toast.success(
              data.source === 'existing' ? 'Texto existente carregado' : 'Texto sugerido pelo LLM',
            )
          } else {
            toast.warning('Sem texto sugerido', {
              description: data.error || 'Sem roteiro disponível pra inferir.',
            })
          }
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha ao sugerir texto.'))
        },
      },
    )
  }

  function onApply() {
    if (!preview) return
    apply.mutate(
      { channel, systemVideo, thumbPath: preview.thumbPath },
      {
        onSuccess: () => {
          toast.success('Thumbnail aplicada')
          setPreview(null)
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha ao aplicar.'))
        },
      },
    )
  }

  function onDiscard() {
    setPreview(null)
  }

  function onShowInPlayer() {
    // Ask the VideoPlayer above to pause + seek to 0. The composed MP4
    // starts with the prepended thumbnail frame, so this is effectively
    // "show me the current thumbnail in the actual video". Scroll the
    // player into view so the user actually sees it.
    window.dispatchEvent(new CustomEvent('prism:reset-player'))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className={styles.shell}>
      <div className={styles.headerActions}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leadingIcon={<Eye />}
          onClick={onShowInPlayer}
        >
          Ver thumb no vídeo
        </Button>
      </div>

      <div className={styles.row}>
        <form className={styles.form} onSubmit={onGenerate} noValidate>
          <div className={styles.field}>
            <span className={styles.label}>Estilo</span>
            <ThumbnailStyleCarousel
              value={style}
              onValueChange={setStyle}
              accentColor={highlightColor}
            />
            <span className={styles.hint}>Vazio = usa o padrão do canal.</span>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="thumb-text">Texto</label>
            <Input
              id="thumb-text"
              placeholder="Ex: ESPAÇO *DERRETE* TUDO"
              value={text}
              onChange={(e) => {
                setText(e.target.value)
              }}
            />
            <span className={styles.hint}>
              Use *asteriscos* pra destacar uma palavra com a cor escolhida (ex.: ESPAÇO *DERRETE* TUDO).
              Sem asteriscos, escolhemos uma palavra automaticamente.
            </span>
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Cor de destaque</span>
            <div className={styles.colorRow}>
              <input
                type="color"
                aria-label="Cor de destaque"
                className={styles.colorPicker}
                value={highlightColor}
                onChange={(e) => {
                  setHighlightColor(e.target.value.toUpperCase())
                  setHexInput(e.target.value.toUpperCase())
                  setHexError(null)
                }}
              />
              <Input
                aria-label="Hex da cor"
                className={styles.colorHex}
                value={hexInput}
                onChange={(e) => {
                  setHexInput(e.target.value)
                }}
                onBlur={(e) => {
                  handleHexBlur(e.target.value)
                }}
              />
            </div>
            {hexError ? <span className={styles.errorText}>{hexError}</span> : null}
          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              leadingIcon={<Wand2 />}
              disabled={suggest.isPending}
              onClick={onSuggest}
            >
              {suggest.isPending ? 'Sugerindo...' : 'Sugerir texto'}
            </Button>
            <Button
              type="submit"
              size="sm"
              leadingIcon={<Sparkles />}
              disabled={generate.isPending || !text.trim()}
            >
              {generate.isPending ? 'Gerando...' : 'Gerar prévia'}
            </Button>
          </div>

          {generate.isError && !preview ? (
            <span className={styles.errorText}>{errorMessage(generate.error)}</span>
          ) : null}
        </form>
      </div>

      {preview ? (
        <section className={styles.previewSection}>
          <div className={styles.previewMeta}>
            <span>
              <ImageIcon aria-hidden style={{ width: 12, height: 12, marginRight: 4 }} />
              Prévia gerada
            </span>
            <span className={styles.previewMetaValue}>{preview.durationS.toFixed(1)}s</span>
          </div>
          <div className={styles.previewBox}>
            <img src={preview.url} alt="Prévia da nova thumbnail" />
          </div>
          <div className={styles.previewActions}>
            <Button
              size="sm"
              leadingIcon={<Check />}
              disabled={apply.isPending}
              onClick={onApply}
            >
              {apply.isPending ? 'Aplicando...' : 'Aplicar como nova thumb'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              leadingIcon={<X />}
              onClick={onDiscard}
              disabled={apply.isPending}
            >
              Descartar
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  )
}
